import { ref, computed } from 'vue';
import { t } from '@/utils/i18n-helper';
import invitacionesService from '@/services/perfil/invitaciones';
import { toError } from '@/services/perfil/_normalize';

/**
 * useStepperButacas — Estado y operaciones del Paso 2 (selección y bloqueo de butacas).
 *
 * Responsabilidades:
 *  - Carga del mapa de asientos para la zona seleccionada.
 *  - Selección / deselección de asientos con límite maxAsientos.
 *  - Bloqueo de asientos en backend y obtención de sessionId.
 *  - Liberación de sesión (borrarSesion).
 *  - Guards: isAsientosEmpty, limitAsientosAlcanzado, puedeAvanzarPasoButacas.
 *
 * @param {import('vue').Ref<Object|null>} evento             Ref del evento activo
 * @param {() => string|number}             getAbonadoId       Función que retorna el ID del abonado
 * @param {import('vue').Ref<Object|null>}   zonaSeleccionada   Ref de la zona elegida en Paso 1
 * @param {import('vue').ComputedRef<number>} maxAsientos       Límite máximo de butacas seleccionables
 */
export function useStepperButacas(evento, getAbonadoId, zonaSeleccionada, maxAsientos) {

    // ─── State ────────────────────────────────────────────────────────────

    /**
     * Respuesta completa del endpoint getAsientos (SVG, metadatos de sector,
     * lista de plazas disponibles, etc.).
     */
    const asientosData = ref(null);
    const loadingAsientos = ref(false);
    const errorAsientos = ref(null);

    /**
     * Asientos que el usuario ha seleccionado antes de confirmar el bloqueo.
     */
    const asientosSeleccionados = ref([]);

    const loadingBloquear = ref(false);
    const errorBloquear = ref(null);

    /**
     * ID de sesión retornado por bloquearAsientos. Se necesita para todas las
     * llamadas posteriores y para el CuentaAtras legacy.
     */
    const sessionId = ref(null);

    // ─── Computed ─────────────────────────────────────────────────────────

    /**
     * `true` si los asientos se cargaron sin error pero no hay datos útiles.
     */
    const isAsientosEmpty = computed(
        () => !loadingAsientos.value && !errorAsientos.value && !asientosData.value
    );

    /**
     * `true` cuando el usuario ha alcanzado el límite de asientos seleccionables.
     */
    const limitAsientosAlcanzado = computed(
        () => maxAsientos.value > 0 && asientosSeleccionados.value.length >= maxAsientos.value
    );

    /**
     * Paso 2 → puede avanzar si hay al menos 1 asiento seleccionado.
     */
    const puedeAvanzarPasoButacas = computed(
        () => asientosSeleccionados.value.length > 0
    );

    // ─── API: carga de asientos ───────────────────────────────────────────

    /**
     * Carga los asientos disponibles para la zona seleccionada.
     */
    const fetchAsientos = async() => {
        const abonadoId = getAbonadoId();
        if (!abonadoId || !evento.value || !zonaSeleccionada.value) return;

        loadingAsientos.value = true;
        errorAsientos.value = null;
        try {
            const result = await invitacionesService.getAsientos(
                abonadoId,
                evento.value.evento_id,
                evento.value.sesion_id,
                zonaSeleccionada.value.id,
            );
            asientosData.value = result;
        } catch (err) {
            const normalized = toError(err);
            errorAsientos.value = normalized.message ??
                t('perfil.invitaciones.stepper.error_asientos', {}, 'Error al cargar el mapa de asientos.');
            asientosData.value = null;
            if (process.env.NODE_ENV !== 'production') {
                console.error('[useStepperButacas] Error al obtener asientos:', err);
            }
        } finally {
            loadingAsientos.value = false;
        }
    };

    // ─── Selección de asientos ────────────────────────────────────────────

    /**
     * Añade o quita un asiento de la selección, respetando maxAsientos.
     *
     * @param {Object} asiento  Objeto asiento del mapa
     * @returns {{ added: boolean, removed: boolean, limitReached: boolean }}
     */
    const toggleAsiento = asiento => {
        const idx = asientosSeleccionados.value.findIndex(a => a.id === asiento.id);
        if (idx !== -1) {
            asientosSeleccionados.value.splice(idx, 1);
            return { added: false, removed: true, limitReached: false };
        }
        if (asientosSeleccionados.value.length >= maxAsientos.value) {
            return { added: false, removed: false, limitReached: true };
        }
        asientosSeleccionados.value.push(asiento);
        return { added: true, removed: false, limitReached: false };
    };

    /**
     * Quita un asiento concreto de la selección por su ID.
     * @param {string|number} asientoId
     */
    const quitarAsiento = asientoId => {
        const idx = asientosSeleccionados.value.findIndex(a => a.id === asientoId);
        if (idx !== -1) {
            asientosSeleccionados.value.splice(idx, 1);
        }
    };

    /**
     * Limpia todos los asientos seleccionados.
     */
    const limpiarAsientosSeleccionados = () => {
        asientosSeleccionados.value = [];
    };

    // ─── Bloqueo de asientos ──────────────────────────────────────────────

    /**
     * Bloquea los asientos seleccionados en el backend y obtiene el sessionId.
     * En caso de error, limpia la selección y recarga el mapa automáticamente.
     *
     * @returns {Promise<Object>}  Resultado del backend (contiene session_id)
     * @throws {Error}  Si el bloqueo falla (el orchestrator NO debe avanzar de paso)
     */
    const bloquearAsientos = async() => {
        const abonadoId = getAbonadoId();
        if (!abonadoId || !evento.value) throw new Error('Missing context');

        loadingBloquear.value = true;
        errorBloquear.value = null;

        try {
            const zona = zonaSeleccionada.value;
            const payload = {
                filters:    {},
                abonado_id: String(abonadoId),
                evento_id:  String(evento.value.evento_id),
                sesion_id:  String(evento.value.sesion_id),
                asientos:   asientosSeleccionados.value.map(a => ({
                    fila:        a.row    ?? a.fila    ?? '',
                    asiento:     a.number ?? a.asiento ?? a.numero ?? '',
                    zona_id:     zona?.id    ?? null,
                    zona_nombre: zona?.name  ?? zona?.nombre ?? '',
                })),
            };
            const result = await invitacionesService.bloquearAsientos(
                abonadoId,
                evento.value.evento_id,
                evento.value.sesion_id,
                payload,
            );
            sessionId.value = result.session_id;
            return result;
        } catch (err) {
            const normalized = toError(err);
            errorBloquear.value = normalized.message ??
                t('perfil.invitaciones.stepper.error_bloquear', {}, 'Error al reservar los asientos. Por favor, comprueba tu selección e inténtalo de nuevo.');
            limpiarAsientosSeleccionados();
            await fetchAsientos();
            if (process.env.NODE_ENV !== 'production') {
                console.error('[useStepperButacas] Error al bloquear asientos:', err);
            }
            throw err;
        } finally {
            loadingBloquear.value = false;
        }
    };

    // ─── Liberación de sesión ─────────────────────────────────────────────

    /**
     * Llama al backend para borrar la sesión activa y liberar los asientos.
     * Idempotente: si no hay sessionId, retorna sin hacer nada.
     * Siempre limpia el sessionId local al finalizar.
     */
    const liberarSesion = async() => {
        if (!sessionId.value) return;
        const currentSessionId = sessionId.value;
        sessionId.value = null;
        try {
            await invitacionesService.borrarSesion(currentSessionId);
        } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('[useStepperButacas] Error al liberar sesión:', err);
            }
        }
    };

    /**
     * Resetea el estado de sesión sin llamar al backend.
     * Útil cuando la sesión ya fue consumida (p.ej. tras procesarCompra).
     */
    const limpiarSessionId = () => {
        sessionId.value = null;
    };

    // ─── Reset ────────────────────────────────────────────────────────────
    const reset = () => {
        asientosData.value = null;
        asientosSeleccionados.value = [];
        errorAsientos.value = null;
        errorBloquear.value = null;
        sessionId.value = null;
    };

    // ─── API ──────────────────────────────────────────────────────────────
    return {
        asientosData,
        loadingAsientos,
        errorAsientos,
        asientosSeleccionados,
        loadingBloquear,
        errorBloquear,
        sessionId,
        fetchAsientos,
        toggleAsiento,
        quitarAsiento,
        limpiarAsientosSeleccionados,
        bloquearAsientos,
        liberarSesion,
        limpiarSessionId,
        isAsientosEmpty,
        limitAsientosAlcanzado,
        puedeAvanzarPasoButacas,
        reset,
    };
}
